// src/components/TotalsSummary.js

import React from 'react';
import MetricCard from './MetricCard';
import { Flag } from 'react-feather';
import { TOOLTIP_DEFINITIONS } from '../utils/tooltips';

function TotalsSummary({ totals = {} }) {
  const isRefund = totals.finalTaxOwed < 0;
  const grossIncome = totals.grossIncome || 0;

  // FIX: Ensure this returns a strict Boolean (true/false), never 0
  const hasVal = (val) => {
    const num = parseFloat(val);
    return !isNaN(num) && Math.abs(num) > 0;
  };

  return (
    <>
      <div className="card-header">
        <h2><Flag size={20} className="icon-orange" /> Federal Tax Summary</h2>
      </div>
      
      <div className="totals-summary">
        {/* --- ROW 1: INCOME BREAKDOWN --- */}
        
        {/* 1. Gross Income (Always Show) */}
        <MetricCard
          title="Gross Income"
          value={totals.grossIncome}
          className="has-tooltip"
          data-tooltip={TOOLTIP_DEFINITIONS['Gross Income']}
        />

        {/* 2. Business Income (Conditional) */}
        {hasVal(totals.netBusinessIncome) && (
          <MetricCard
            title="Business Income (Sch C)"
            value={totals.netBusinessIncome}
            className="neutral has-tooltip"
            data-tooltip="Net Profit or Loss from your business."
            change={grossIncome ? totals.netBusinessIncome / grossIncome : null}
          />
        )}

        {/* 3. Rental/K-1 Income (Conditional) */}
        {hasVal(totals.netRentalIncome) && (
          <MetricCard
            title="Rental & Passive (Sch E)"
            value={totals.netRentalIncome}
            className="neutral has-tooltip"
            data-tooltip="Net Income or Loss from Rentals, Partnerships, and S-Corps."
            change={grossIncome ? totals.netRentalIncome / grossIncome : null}
          />
        )}

        {/* 4. Capital Gains (Conditional) */}
        {hasVal(totals.line7) && (
          <MetricCard
            title="Capital Gains (Sch D)"
            value={totals.line7}
            className="neutral has-tooltip"
            data-tooltip="Net gain or loss from the sale of assets."
            change={grossIncome ? totals.line7 / grossIncome : null}
          />
        )}

        {/* --- ROW 2: CALCULATIONS --- */}

        <MetricCard
          title="Adjusted Gross (AGI)"
          value={totals.agi}
          className="has-tooltip"
          data-tooltip={TOOLTIP_DEFINITIONS['Adjusted Gross (AGI)']}
        />
        
        <MetricCard
          title="Taxable Income"
          value={totals.taxableIncome}
          className="neutral has-tooltip"
          data-tooltip={TOOLTIP_DEFINITIONS['Taxable Income']}
          change={grossIncome ? totals.taxableIncome / grossIncome : null}
        />

        <MetricCard
          title="Total Deductions"
          value={totals.finalDeduction}
          className="refund has-tooltip"
          data-tooltip={TOOLTIP_DEFINITIONS['Total Deductions']}
          change={grossIncome ? totals.finalDeduction / grossIncome : null}
        />

        {/* --- ROW 3: TAXES & CREDITS --- */}

        <MetricCard
          title="Total Tax Liability"
          value={totals.taxLiability}
          className="tax has-tooltip"
          data-tooltip={TOOLTIP_DEFINITIONS['Total Tax Due']}
          change={grossIncome ? (totals.taxLiability / grossIncome) * -1 : null}
        />

        {/* Self-Employment Tax (Conditional - Important for Schedule C) */}
        {hasVal(totals.selfEmploymentTax) && (
          <MetricCard
            title="Self-Employment Tax"
            value={totals.selfEmploymentTax}
            className="tax has-tooltip"
            data-tooltip="Social Security and Medicare tax on business income (Schedule SE)."
            change={grossIncome ? (totals.selfEmploymentTax / grossIncome) * -1 : null}
          />
        )}

        <MetricCard
          title="Total Credits"
          value={totals.totalCredits}
          className="refund has-tooltip"
          data-tooltip={TOOLTIP_DEFINITIONS['Total Credits']}
          change={grossIncome ? totals.totalCredits / grossIncome : null}
        />

        <MetricCard
          title="Taxes Withheld/Paid"
          value={totals.taxesWithheld + (totals.line26 || 0)} // W2 + Estimated Payments
          className="tax has-tooltip"
          data-tooltip="Total federal tax already paid via W-2 withholding or estimated payments."
          change={grossIncome ? ((totals.taxesWithheld + (totals.line26 || 0)) / grossIncome) * -1 : null}
        />

        {/* --- ROW 4: FINAL RESULT --- */}
        <MetricCard
          title={isRefund ? 'Federal Refund' : 'Federal Tax Owed'}
          value={Math.abs(totals.finalTaxOwed)}
          className={`${isRefund ? 'refund' : 'tax'} has-tooltip`}
          data-tooltip={TOOLTIP_DEFINITIONS[isRefund ? 'Federal Refund' : 'Federal Tax Owed']}
          change={grossIncome ? totals.finalTaxOwed / grossIncome : null}
        />
      </div>
    </>
  );
}

export default TotalsSummary;